// iter106 — Feasibility Profile page.
// Per-tenant "company capabilities" that iter107 will cross-check tender
// requirements against. Tabbed layout with 5 sections:
//   1. Certificates (list + upload + delete)
//   2. Empanelments (list + upload + delete)
//   3. Financials — turnover 3 FYs + EMD/BG limits
//   4. Approval matrix (view-only mirror of the hardcoded ladder, for now)
//   5. Blacklist + Past clients
//
// RBAC: everyone in tenant can view; TE + VP + President can edit.

import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import useAuthStore from '../store/authStore';
import { Certificate as CertIcon, Buildings, CurrencyInr, Shield, Users, Trash, Upload, PencilSimple, FloppyDisk } from '@phosphor-icons/react';

import { confirm } from '../components/ConfirmDialog';
import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EDIT_ROLES = new Set(['Tender Executive', 'Vice President', 'President']);
const inrCr = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const authedFileUrl = (url) => {
  if (!url) return '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const base = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  return token ? `${base}${base.includes('?') ? '&' : '?'}auth=${encodeURIComponent(token)}` : base;
};

const TABS = [
  { key: 'certs', label: 'Certificates', icon: CertIcon },
  { key: 'empanelments', label: 'Empanelments', icon: Buildings },
  { key: 'financials', label: 'Financials', icon: CurrencyInr },
  { key: 'approval', label: 'Approval Matrix', icon: Shield },
  { key: 'other', label: 'Blacklist & Clients', icon: Users },
];

export default function FeasibilityProfile() {
  const { user } = useAuthStore();
  const canEdit = EDIT_ROLES.has(user?.role);
  const [prof, setProf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('certs');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const reload = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/feasibility-profile`);
      setProf(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const saveNonFileFields = async (patch) => {
    setSaving(true); setError('');
    try {
      const payload = {
        turnover_last_3_fy: prof.turnover_last_3_fy,
        max_emd_amount: prof.max_emd_amount,
        max_bg_amount: prof.max_bg_amount,
        approval_matrix: prof.approval_matrix,
        blacklist: prof.blacklist,
        past_clients: prof.past_clients,
        ...patch,
      };
      const res = await axios.put(`${API}/feasibility-profile`, payload);
      setProf(res.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-[#6D8E87]">Loading profile…</div>;
  if (!prof) return <div className="p-6 text-rose-700">{error || 'Failed to load profile'}</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="feasibility-profile-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Feasibility Profile</h1>
          <p className="text-sm text-[#3B5A54] max-w-2xl mt-1">
            Company capabilities the AI cross-checks new tenders against. Keep these current so auto-feasibility stays reliable.
          </p>
        </div>
        <div className="text-right text-[10px] text-[#6D8E87]">
          {prof.updated_by_name && <>Last updated by <b>{prof.updated_by_name}</b><br /></>}
          {prof.updated_at?.slice(0, 10)}
        </div>
      </div>

      {!canEdit && (
        <div className="mb-4 px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-800 text-xs" data-testid="feasibility-readonly-banner">
          You have read-only access to the Feasibility Profile. Only TE, VP, or President can make changes.
        </div>
      )}
      {error && (
        <div className="mb-4 px-3 py-2 rounded border border-rose-200 bg-rose-50 text-rose-800 text-xs" data-testid="feasibility-error">{error}</div>
      )}
      {savedFlash && (
        <div className="mb-4 px-3 py-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs" data-testid="feasibility-saved">Saved ✓</div>
      )}

      {/* Tab strip */}
      <div className="border-b border-[#D5E8E4] mb-5 flex gap-1 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${
                active ? 'border-[#00a69c] text-[#00a69c]' : 'border-transparent text-[#6D8E87] hover:text-[#052A23]'
              }`}
              data-testid={`feasibility-tab-${t.key}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {tab === 'certs' && <CertificatesTab prof={prof} canEdit={canEdit} onChange={reload} />}
      {tab === 'empanelments' && <EmpanelmentsTab prof={prof} canEdit={canEdit} onChange={reload} />}
      {tab === 'financials' && <FinancialsTab prof={prof} setProf={setProf} canEdit={canEdit} saving={saving} onSave={saveNonFileFields} />}
      {tab === 'approval' && <ApprovalMatrixTab prof={prof} setProf={setProf} canEdit={canEdit} saving={saving} onSave={saveNonFileFields} />}
      {tab === 'other' && <OtherTab prof={prof} setProf={setProf} canEdit={canEdit} saving={saving} onSave={saveNonFileFields} />}
    </div>
  );
}

/* ─────────── Certificates ─────────── */
function CertificatesTab({ prof, canEdit, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', cert_no: '', issuer: '', valid_till: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!file) { setErr('Please choose a file'); return; }
    if (!form.name.trim()) { setErr('Certificate name is required'); return; }
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const qs = new URLSearchParams(form).toString();
      await axios.post(`${API}/feasibility-profile/certificate?${qs}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowAdd(false); setForm({ name: '', cert_no: '', issuer: '', valid_till: '' }); setFile(null);
      onChange();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!await confirm({ message: 'Delete this certificate?' })) return;
    try { await axios.delete(`${API}/feasibility-profile/certificate/${id}`); onChange(); }
    catch (e) { notify(e?.response?.data?.detail || e.message); }
  };

  return (
    <div>
      {canEdit && !showAdd && (
        <button type="button" onClick={() => setShowAdd(true)}
          className="mb-3 text-sm px-3 py-1.5 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3]"
          data-testid="feasibility-add-cert-btn">+ Add certificate</button>
      )}
      {showAdd && (
        <div className="rounded-lg border border-[#D5E8E4] bg-white p-4 mb-4" data-testid="feasibility-cert-form">
          <h3 className="text-sm font-bold text-[#052A23] mb-2">New certificate</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="ISO 9001:2015" testid="cert-name" />
            <Input label="Certificate #" value={form.cert_no} onChange={(v) => setForm({ ...form, cert_no: v })} testid="cert-no" />
            <Input label="Issuer" value={form.issuer} onChange={(v) => setForm({ ...form, issuer: v })} placeholder="BSI, TÜV, DNV…" testid="cert-issuer" />
            <Input label="Valid till" type="date" value={form.valid_till} onChange={(v) => setForm({ ...form, valid_till: v })} testid="cert-valid" />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="cert-file-input" />
            <button type="button" onClick={submit} disabled={uploading}
              className="text-xs px-3 py-1.5 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
              data-testid="cert-save-btn">
              <Upload className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />{uploading ? 'Uploading…' : 'Upload'}
            </button>
            <button type="button" onClick={() => { setShowAdd(false); setErr(''); }} className="text-xs px-2 py-1.5 rounded border">Cancel</button>
            {err && <span className="text-xs text-rose-700">{err}</span>}
          </div>
        </div>
      )}
      <ListTable
        rows={prof.certificates || []}
        emptyMsg="No certificates uploaded yet. Click + Add certificate."
        testidPrefix="cert-row"
        columns={[
          { header: 'Name', render: (r) => <b className="text-[#052A23]">{r.name}</b> },
          { header: 'Cert #', render: (r) => r.cert_no || '—' },
          { header: 'Issuer', render: (r) => r.issuer || '—' },
          { header: 'Valid till', render: (r) => r.valid_till || '—' },
          { header: 'File', render: (r) => r.file_url ? <a href={authedFileUrl(r.file_url)} target="_blank" rel="noreferrer" className="text-indigo-700 underline text-xs">Open</a> : '—' },
        ]}
        onDelete={canEdit ? del : null}
      />
    </div>
  );
}

/* ─────────── Empanelments ─────────── */
function EmpanelmentsTab({ prof, canEdit, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ authority: '', empanelment_no: '', valid_till: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!file) { setErr('Please choose a file'); return; }
    if (!form.authority.trim()) { setErr('Authority is required'); return; }
    setUploading(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const qs = new URLSearchParams(form).toString();
      await axios.post(`${API}/feasibility-profile/empanelment?${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowAdd(false); setForm({ authority: '', empanelment_no: '', valid_till: '' }); setFile(null);
      onChange();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };
  const del = async (id) => {
    if (!await confirm({ message: 'Delete this empanelment?' })) return;
    try { await axios.delete(`${API}/feasibility-profile/empanelment/${id}`); onChange(); }
    catch (e) { notify(e?.response?.data?.detail || e.message); }
  };
  return (
    <div>
      {canEdit && !showAdd && (
        <button type="button" onClick={() => setShowAdd(true)}
          className="mb-3 text-sm px-3 py-1.5 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3]"
          data-testid="feasibility-add-emp-btn">+ Add empanelment</button>
      )}
      {showAdd && (
        <div className="rounded-lg border border-[#D5E8E4] bg-white p-4 mb-4" data-testid="feasibility-emp-form">
          <h3 className="text-sm font-bold text-[#052A23] mb-2">New empanelment</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Authority *" value={form.authority} onChange={(v) => setForm({ ...form, authority: v })} placeholder="GeM, DGS&D, AIIMS…" testid="emp-authority" />
            <Input label="Empanelment #" value={form.empanelment_no} onChange={(v) => setForm({ ...form, empanelment_no: v })} testid="emp-no" />
            <Input label="Valid till" type="date" value={form.valid_till} onChange={(v) => setForm({ ...form, valid_till: v })} testid="emp-valid" />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="emp-file-input" />
            <button type="button" onClick={submit} disabled={uploading}
              className="text-xs px-3 py-1.5 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
              data-testid="emp-save-btn">
              <Upload className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />{uploading ? 'Uploading…' : 'Upload'}
            </button>
            <button type="button" onClick={() => { setShowAdd(false); setErr(''); }} className="text-xs px-2 py-1.5 rounded border">Cancel</button>
            {err && <span className="text-xs text-rose-700">{err}</span>}
          </div>
        </div>
      )}
      <ListTable
        rows={prof.empanelments || []}
        emptyMsg="No empanelments uploaded yet."
        testidPrefix="emp-row"
        columns={[
          { header: 'Authority', render: (r) => <b className="text-[#052A23]">{r.authority}</b> },
          { header: 'Empanelment #', render: (r) => r.empanelment_no || '—' },
          { header: 'Valid till', render: (r) => r.valid_till || '—' },
          { header: 'File', render: (r) => r.file_url ? <a href={authedFileUrl(r.file_url)} target="_blank" rel="noreferrer" className="text-indigo-700 underline text-xs">Open</a> : '—' },
        ]}
        onDelete={canEdit ? del : null}
      />
    </div>
  );
}

/* ─────────── Financials ─────────── */
function FinancialsTab({ prof, setProf, canEdit, saving, onSave }) {
  const avg = useMemo(() => {
    const arr = prof.turnover_last_3_fy || [];
    if (!arr.length) return 0;
    return arr.reduce((s, x) => s + (Number(x.turnover_inr) || 0), 0) / arr.length;
  }, [prof.turnover_last_3_fy]);

  const setFy = (idx, val) => {
    const arr = [...(prof.turnover_last_3_fy || [])];
    arr[idx] = { ...arr[idx], turnover_inr: Number(val) || 0 };
    setProf({ ...prof, turnover_last_3_fy: arr });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#D5E8E4] bg-white p-4">
        <h3 className="text-sm font-bold text-[#052A23] mb-3">Turnover (last 3 FYs, in ₹)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(prof.turnover_last_3_fy || []).map((t, i) => (
            <div key={t.fy}>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">FY {t.fy}</label>
              <input type="number" value={t.turnover_inr || ''} disabled={!canEdit}
                onChange={(e) => setFy(i, e.target.value)}
                className="w-full border border-[#D5E8E4] rounded px-2 py-1.5 text-sm bg-white disabled:bg-gray-50"
                data-testid={`turnover-fy-${i}`} />
              <div className="text-[10px] text-[#6D8E87] mt-1">{inrCr(t.turnover_inr)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs text-[#3B5A54]">Average: <b>{inrCr(avg)}</b></div>
      </div>

      <div className="rounded-lg border border-[#D5E8E4] bg-white p-4">
        <h3 className="text-sm font-bold text-[#052A23] mb-3">EMD / BG hard limits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Max EMD amount (₹)</label>
            <input type="number" value={prof.max_emd_amount || ''} disabled={!canEdit}
              onChange={(e) => setProf({ ...prof, max_emd_amount: Number(e.target.value) || 0 })}
              className="w-full border border-[#D5E8E4] rounded px-2 py-1.5 text-sm bg-white disabled:bg-gray-50"
              data-testid="max-emd" />
            <div className="text-[10px] text-[#6D8E87] mt-1">{inrCr(prof.max_emd_amount)}</div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Max BG amount (₹)</label>
            <input type="number" value={prof.max_bg_amount || ''} disabled={!canEdit}
              onChange={(e) => setProf({ ...prof, max_bg_amount: Number(e.target.value) || 0 })}
              className="w-full border border-[#D5E8E4] rounded px-2 py-1.5 text-sm bg-white disabled:bg-gray-50"
              data-testid="max-bg" />
            <div className="text-[10px] text-[#6D8E87] mt-1">{inrCr(prof.max_bg_amount)}</div>
          </div>
        </div>
      </div>

      {canEdit && (
        <button type="button" onClick={() => onSave({})} disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
          data-testid="financials-save-btn">
          <FloppyDisk className="inline w-4 h-4 -mt-0.5 mr-1" />{saving ? 'Saving…' : 'Save financials'}
        </button>
      )}
    </div>
  );
}

/* ─────────── Approval Matrix ─────────── */
function ApprovalMatrixTab({ prof, setProf, canEdit, saving, onSave }) {
  const rows = prof.approval_matrix || [];
  const setRow = (i, patch) => {
    const arr = [...rows]; arr[i] = { ...arr[i], ...patch };
    setProf({ ...prof, approval_matrix: arr });
  };
  return (
    <div>
      <p className="text-xs text-[#6D8E87] mb-3">
        Value bands and their approver role. Tenders auto-route by estimated value.
      </p>
      <div className="rounded-lg border border-[#D5E8E4] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4FAF8] text-[11px] uppercase tracking-wide text-[#6D8E87]">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Up to value (₹)</th>
              <th className="text-left px-3 py-2 font-semibold">Approver role</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#EEF6F3]">
                <td className="px-3 py-2">
                  <input type="number" value={r.up_to_value_inr || 0} disabled={!canEdit}
                    onChange={(e) => setRow(i, { up_to_value_inr: Number(e.target.value) || 0 })}
                    className="w-40 border border-[#D5E8E4] rounded px-2 py-1 text-sm disabled:bg-gray-50"
                    data-testid={`matrix-value-${i}`} />
                  <span className="ml-2 text-[10px] text-[#6D8E87]">{inrCr(r.up_to_value_inr)}</span>
                </td>
                <td className="px-3 py-2">
                  <select value={r.approver_role} disabled={!canEdit}
                    onChange={(e) => setRow(i, { approver_role: e.target.value })}
                    className="border border-[#D5E8E4] rounded px-2 py-1 text-sm bg-white disabled:bg-gray-50"
                    data-testid={`matrix-role-${i}`}>
                    {['Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President'].map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <button type="button" onClick={() => onSave({})} disabled={saving}
          className="mt-4 text-sm px-4 py-2 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
          data-testid="matrix-save-btn">
          <FloppyDisk className="inline w-4 h-4 -mt-0.5 mr-1" />{saving ? 'Saving…' : 'Save approval matrix'}
        </button>
      )}
    </div>
  );
}

/* ─────────── Blacklist + Past Clients ─────────── */
function OtherTab({ prof, setProf, canEdit, saving, onSave }) {
  const addBlacklist = () => setProf({
    ...prof, blacklist: [...(prof.blacklist || []), { id: crypto.randomUUID(), authority: '', reason: '', from_date: '', till_date: '', active: true }],
  });
  const rmBlacklist = (i) => setProf({ ...prof, blacklist: prof.blacklist.filter((_, x) => x !== i) });
  const setBL = (i, patch) => { const arr = [...prof.blacklist]; arr[i] = { ...arr[i], ...patch }; setProf({ ...prof, blacklist: arr }); };

  const addClient = () => setProf({
    ...prof, past_clients: [...(prof.past_clients || []), { id: crypto.randomUUID(), client_name: '', product_supplied: '', year: '', value_inr: 0 }],
  });
  const rmClient = (i) => setProf({ ...prof, past_clients: prof.past_clients.filter((_, x) => x !== i) });
  const setPC = (i, patch) => { const arr = [...prof.past_clients]; arr[i] = { ...arr[i], ...patch }; setProf({ ...prof, past_clients: arr }); };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#D5E8E4] bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#052A23]">Blacklist / debarment history</h3>
          {canEdit && <button type="button" onClick={addBlacklist} className="text-xs px-2 py-1 rounded border" data-testid="add-blacklist-btn">+ Add</button>}
        </div>
        {(prof.blacklist || []).length === 0 && <p className="text-xs text-[#6D8E87]">No blacklist entries. (Good news!)</p>}
        {(prof.blacklist || []).map((b, i) => (
          <div key={b.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2 items-center">
            <input placeholder="Authority" value={b.authority} disabled={!canEdit} onChange={(e) => setBL(i, { authority: e.target.value })} className="text-sm border rounded px-2 py-1 md:col-span-2" data-testid={`bl-authority-${i}`} />
            <input placeholder="Reason" value={b.reason} disabled={!canEdit} onChange={(e) => setBL(i, { reason: e.target.value })} className="text-sm border rounded px-2 py-1 md:col-span-2" data-testid={`bl-reason-${i}`} />
            <input type="date" value={b.from_date} disabled={!canEdit} onChange={(e) => setBL(i, { from_date: e.target.value })} className="text-sm border rounded px-2 py-1" data-testid={`bl-from-${i}`} />
            {canEdit && <button type="button" onClick={() => rmBlacklist(i)} className="text-xs text-rose-700"><Trash className="inline w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-[#D5E8E4] bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#052A23]">Past clients / references</h3>
          {canEdit && <button type="button" onClick={addClient} className="text-xs px-2 py-1 rounded border" data-testid="add-client-btn">+ Add</button>}
        </div>
        {(prof.past_clients || []).length === 0 && <p className="text-xs text-[#6D8E87]">Add past supplies so the AI can validate past-experience clauses.</p>}
        {(prof.past_clients || []).map((c, i) => (
          <div key={c.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2 items-center">
            <input placeholder="Client name" value={c.client_name} disabled={!canEdit} onChange={(e) => setPC(i, { client_name: e.target.value })} className="text-sm border rounded px-2 py-1 md:col-span-2" data-testid={`pc-name-${i}`} />
            <input placeholder="Product supplied" value={c.product_supplied} disabled={!canEdit} onChange={(e) => setPC(i, { product_supplied: e.target.value })} className="text-sm border rounded px-2 py-1 md:col-span-2" data-testid={`pc-product-${i}`} />
            <input placeholder="Year" value={c.year} disabled={!canEdit} onChange={(e) => setPC(i, { year: e.target.value })} className="text-sm border rounded px-2 py-1" data-testid={`pc-year-${i}`} />
            <input type="number" placeholder="Value ₹" value={c.value_inr || ''} disabled={!canEdit} onChange={(e) => setPC(i, { value_inr: Number(e.target.value) || 0 })} className="text-sm border rounded px-2 py-1" data-testid={`pc-value-${i}`} />
            {canEdit && <button type="button" onClick={() => rmClient(i)} className="text-xs text-rose-700"><Trash className="inline w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>

      {canEdit && (
        <button type="button" onClick={() => onSave({})} disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
          data-testid="other-save-btn">
          <FloppyDisk className="inline w-4 h-4 -mt-0.5 mr-1" />{saving ? 'Saving…' : 'Save blacklist & clients'}
        </button>
      )}
    </div>
  );
}

/* ─────────── Shared ─────────── */
function Input({ label, value, onChange, type = 'text', placeholder = '', testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">{label}</label>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-[#D5E8E4] rounded px-2 py-1.5 text-sm bg-white"
        data-testid={testid} />
    </div>
  );
}

function ListTable({ rows, columns, emptyMsg, testidPrefix, onDelete }) {
  return (
    <div className="rounded-lg border border-[#D5E8E4] bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#F4FAF8] text-[11px] uppercase tracking-wide text-[#6D8E87]">
          <tr>
            {columns.map((c, i) => <th key={i} className="text-left px-3 py-2 font-semibold">{c.header}</th>)}
            {onDelete && <th className="px-3 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length + (onDelete ? 1 : 0)} className="px-3 py-6 text-center text-[#6D8E87] text-xs">{emptyMsg}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-[#EEF6F3]" data-testid={`${testidPrefix}-${i}`}>
              {columns.map((c, j) => <td key={j} className="px-3 py-2">{c.render(r)}</td>)}
              {onDelete && (
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => onDelete(r.id)} className="text-xs text-rose-700 hover:text-rose-900" data-testid={`${testidPrefix}-delete-${i}`}>
                    <Trash className="inline w-3.5 h-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
